'use client';

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { BusinessReportAdministrationTab } from './BusinessReportAdministrationTab';
import { BusinessReportCentersTab } from './BusinessReportCentersTab';
import { BusinessReportFinancialCalendarTab } from './BusinessReportFinancialCalendarTab';
import { BusinessReportEmailDistributionTab } from './BusinessReportEmailDistributionTab';
import { businessReportRoute, normalizeBusinessReportPath } from '../lib/business-report-routes';

type AdministrationWorkspaceTab = 'reports' | 'centres' | 'email-distribution' | 'financial-calendar';

const ADMINISTRATION_ROUTE = '/dashboard/business-reports/administration';
const LEGACY_CENTRES_ROUTE = '/dashboard/business-reports/centre-master';
const LEGACY_CALENDAR_ROUTE = '/dashboard/business-reports/financial-calendar';

const WORKSPACE_TABS: Array<{
  key: AdministrationWorkspaceTab;
  label: string;
  description: string;
}> = [
  {
    key: 'reports',
    label: 'Report Administration',
    description: 'Report catalogue and activation',
  },
  {
    key: 'centres',
    label: 'Centre Master',
    description: 'Centres, access and aliases',
  },
  {
    key: 'email-distribution',
    label: 'Email Distribution',
    description: 'Daily delivery and recipients',
  },
  {
    key: 'financial-calendar',
    label: 'Financial Calendar',
    description: 'Financial year and quarters',
  },
];

function isWorkspaceTab(value: string | null): value is AdministrationWorkspaceTab {
  return value === 'reports' || value === 'centres' || value === 'email-distribution' || value === 'financial-calendar';
}

function tabFromLocation(): AdministrationWorkspaceTab {
  if (typeof window === 'undefined') return 'reports';
  const normalizedPath = normalizeBusinessReportPath(window.location.pathname);
  if (normalizedPath === LEGACY_CENTRES_ROUTE) return 'centres';
  if (normalizedPath === LEGACY_CALENDAR_ROUTE) return 'financial-calendar';
  const requestedTab = new URLSearchParams(window.location.search).get('tab');
  return isWorkspaceTab(requestedTab) ? requestedTab : 'reports';
}

function canonicalUrl(tab: AdministrationWorkspaceTab) {
  return businessReportRoute('administration', `tab=${tab}`);
}

export function BusinessReportAdministrationWorkspace({
  active,
  token,
  canExport,
  onTopBarActionsChange,
}: {
  active: boolean;
  token?: string;
  canExport: boolean;
  onTopBarActionsChange?: (actions: ReactNode) => void;
}) {
  const [selectedTab, setSelectedTab] = useState<AdministrationWorkspaceTab>('reports');
  const [locationReady, setLocationReady] = useState(false);

  const syncFromLocation = useCallback((replaceLegacyRoute: boolean) => {
    const nextTab = tabFromLocation();
    setSelectedTab(nextTab);
    if (replaceLegacyRoute || normalizeBusinessReportPath(window.location.pathname) !== ADMINISTRATION_ROUTE || !isWorkspaceTab(new URLSearchParams(window.location.search).get('tab'))) {
      window.history.replaceState({}, '', canonicalUrl(nextTab));
    }
  }, []);

  useEffect(() => {
    if (!active) {
      setLocationReady(false);
      return undefined;
    }
    syncFromLocation(
      normalizeBusinessReportPath(window.location.pathname) === LEGACY_CENTRES_ROUTE
      || normalizeBusinessReportPath(window.location.pathname) === LEGACY_CALENDAR_ROUTE,
    );
    setLocationReady(true);
    const handlePopState = () => syncFromLocation(false);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [active, syncFromLocation]);

  if (!active) return null;

  const selectTab = (tab: AdministrationWorkspaceTab) => {
    if (tab === selectedTab && normalizeBusinessReportPath(window.location.pathname) === ADMINISTRATION_ROUTE) return;
    setSelectedTab(tab);
    window.history.pushState({}, '', canonicalUrl(tab));
  };

  return (
    <section className="business-admin-workspace" aria-label="Business Reports administration workspace">
      <nav className="business-admin-workspace-tabs" role="tablist" aria-label="Report administration sections">
        {WORKSPACE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            id={`business-admin-tab-${tab.key}`}
            className={selectedTab === tab.key ? 'active' : ''}
            role="tab"
            aria-selected={selectedTab === tab.key}
            aria-controls={`business-admin-panel-${tab.key}`}
            tabIndex={selectedTab === tab.key ? 0 : -1}
            onClick={() => selectTab(tab.key)}
          >
            <strong>{tab.label}</strong>
            <span>{tab.description}</span>
          </button>
        ))}
      </nav>

      <div
        className="business-admin-workspace-content"
        id={`business-admin-panel-${selectedTab}`}
        role="tabpanel"
        aria-labelledby={`business-admin-tab-${selectedTab}`}
      >
        <BusinessReportAdministrationTab active={locationReady && selectedTab === 'reports'} token={token} canExport={canExport} onTopBarActionsChange={onTopBarActionsChange} />
        <BusinessReportCentersTab active={locationReady && selectedTab === 'centres'} token={token} onTopBarActionsChange={onTopBarActionsChange} />
        <BusinessReportEmailDistributionTab active={locationReady && selectedTab === 'email-distribution'} token={token} onTopBarActionsChange={onTopBarActionsChange} />
        <BusinessReportFinancialCalendarTab active={locationReady && selectedTab === 'financial-calendar'} token={token} onTopBarActionsChange={onTopBarActionsChange} />
      </div>
    </section>
  );
}
